/**
 * Node's copy of the notification battery-feed hoist (field-crypto plan
 * 8b.2). The marked region is byte-identical to
 * `convex-silo/notifications.ts.template`
 * (tests/notifications/notificationBatteryFeedParity.test.ts): the silo runs
 * it on plaintext writes and in the hoist backfill, Node on sealed writes and
 * opens.
 */

// NOTIFICATION_BATTERY_FEED_START
/**
 * The plaintext `batteryRunwayFeed` column a notification carries beside its
 * (possibly sealed) `extraFields` (plan 8b.2). One pure helper, byte-identical
 * in `src/features/notifications/notificationBatteryFeed.ts`
 * (tests/notifications/notificationBatteryFeedParity.test.ts): the silo
 * derives the column on every plaintext write and in the backfill, Node on
 * every sealed write, so the feed-sync mutation can gate and retire battery
 * alert rows without opening a sealed blob.
 *
 * - `state`: the hidden per-device state row (source `conflict_analysis`,
 *   `extraFields.kind` `battery_runway_state`). A copy of
 *   `extraFields.batteryRunway`'s version (a positive safe integer, else 0),
 *   active flag, live alert id and pending-retire list (non-empty strings,
 *   newest {@link NOTIFICATION_BATTERY_FEED_MAX_PENDING}).
 * - `alert`: a battery-runway feed row (source `device_health`, category
 *   `battery_runway`).
 *
 * On a sealed state row the silo changes only the column (it clears
 * `pendingRetire`), so an open puts the column's `pendingRetire` back into
 * `extraFields.batteryRunway` ({@link notificationExtraFieldsWithBatteryFeed}).
 */
export const NOTIFICATION_BATTERY_FEED_MAX_PENDING = 16;

export type NotificationBatteryFeed =
  | { role: "state"; version: number; active: boolean; alertId?: string; pendingRetire: string[] }
  | { role: "alert" };

function isNotificationBatteryObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

/** The column for a notification document, or undefined when it is not a battery-runway row. */
export function notificationBatteryFeedOf(doc: Record<string, unknown>): NotificationBatteryFeed | undefined {
  const extraFields = isNotificationBatteryObject(doc.extraFields) ? doc.extraFields : {};
  if (doc.source === "conflict_analysis" && extraFields.kind === "battery_runway_state") {
    const state = isNotificationBatteryObject(extraFields.batteryRunway) ? extraFields.batteryRunway : {};
    const version = typeof state.version === "number" && Number.isSafeInteger(state.version) && state.version > 0
      ? state.version
      : 0;
    const pendingRetire = Array.isArray(state.pendingRetire)
      ? state.pendingRetire
        .filter((id: unknown): id is string => typeof id === "string" && id.length > 0)
        .slice(-NOTIFICATION_BATTERY_FEED_MAX_PENDING)
      : [];
    return {
      role: "state",
      version,
      active: state.active === true,
      ...(typeof state.alertId === "string" && state.alertId.length > 0 ? { alertId: state.alertId } : {}),
      pendingRetire,
    };
  }
  if (doc.source === "device_health" && Array.isArray(doc.categories) && doc.categories.includes("battery_runway")) {
    return { role: "alert" };
  }
  return undefined;
}

/**
 * `extraFields` with a state column's `pendingRetire` put back into
 * `batteryRunway`, the only value the silo changes on a sealed state row.
 * Anything else is returned as-is.
 */
export function notificationExtraFieldsWithBatteryFeed(extraFields: unknown, feed: unknown): unknown {
  if (!isNotificationBatteryObject(feed) || feed.role !== "state" || !Array.isArray(feed.pendingRetire)) {
    return extraFields;
  }
  if (!isNotificationBatteryObject(extraFields) || !isNotificationBatteryObject(extraFields.batteryRunway)) {
    return extraFields;
  }
  return { ...extraFields, batteryRunway: { ...extraFields.batteryRunway, pendingRetire: feed.pendingRetire } };
}
// NOTIFICATION_BATTERY_FEED_END
